import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, AlertTriangle, CheckCircle, Clock, FileText, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import Footer from '../components/Footer';
import EmergencyButton from '../components/EmergencyButton';
import { authFetch } from '../lib/authFetch';
import PageHeader from '../components/PageHeader';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

// Australian Pathology Labs
const AUSTRALIAN_LABS = [
  { id: 'qml', name: 'QML Pathology', regions: ['QLD', 'NSW', 'VIC'] },
  { id: 'sullivan', name: 'Sullivan Nicolaides Pathology', regions: ['QLD', 'NSW'] },
  { id: 'dorevitch', name: 'Dorevitch Pathology', regions: ['VIC'] },
  { id: 'laverty', name: 'Laverty Pathology', regions: ['NSW', 'ACT'] },
  { id: 'clinipath', name: 'Clinipath Pathology', regions: ['WA'] },
  { id: 'clinilabs', name: 'Clinilabs', regions: ['SA'] },
  { id: 'other', name: 'Other Approved Lab', regions: ['ALL'] }
];

function DrugScreenPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  
  const [activeRequest, setActiveRequest] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [screenHistory, setScreenHistory] = useState([]);

  // Fetch active request on mount
  React.useEffect(() => {
    fetchActiveRequest();
    fetchScreenHistory();
  }, []);

  const fetchActiveRequest = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/drug-screens/active`);
      if (response.ok) {
        const data = await response.json();
        setActiveRequest(data);
      }
    } catch (error) {
      console.error('Error fetching drug screen request:', error);
    }
  };

  const fetchScreenHistory = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/drug-screens`);
      if (response.ok) {
        const data = await response.json();
        setScreenHistory(data);
      }
    } catch (error) {
      console.error('Error fetching screen history:', error);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an image (JPG, PNG, GIF) or PDF file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }

    setIsUploading(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        setUploadedFile({
          name: file.name,
          type: file.type,
          data: base64,
          preview: file.type.startsWith('image/') ? base64 : null
        });
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process file');
      setIsUploading(false);
    }
  };

  const handleSubmitResults = async () => {
    if (!uploadedFile || !activeRequest) {
      toast.error('Please upload your results first');
      return;
    }

    setIsUploading(true);

    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/drug-screens/${activeRequest.request_id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
          result_file: uploadedFile.data,
          file_name: uploadedFile.name,
          file_type: uploadedFile.type
        })
      });

      if (response.ok) {
        toast.success('Results submitted successfully!');
        setUploadedFile(null);
        fetchActiveRequest();
        fetchScreenHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit results');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('An error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  // Calculate time remaining for deadline
  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate - now;
    
    if (diff <= 0) return { expired: true, text: 'EXPIRED' };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { 
      expired: false, 
      text: `${hours}h ${minutes}m remaining`,
      hours,
      minutes
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <PageHeader title="Drug Screen" />

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('drugScreen.title')}
          </h1>
          <p className="text-sm text-blue-100 mb-6">Manage your drug screening requests and results</p>

          {/* Active Request Card */}
          {activeRequest && (
            <div className="bg-red-600 rounded-xl p-6 mb-6 text-white relative overflow-hidden" data-testid="active-drug-screen">
              <div className="absolute inset-0 bg-red-500 animate-pulse opacity-20" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-white/20 rounded-full">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="bg-white text-red-600 text-xs font-black px-3 py-1 rounded-full uppercase">
                      {t('notifications.urgent')}
                    </span>
                    <h2 className="text-xl font-bold mt-1">{t('notifications.drugScreenRequest')}</h2>
                  </div>
                </div>

                <p className="text-white/90 mb-4">
                  {t('notifications.attendWithin24Hours')}
                </p>

                {/* Time Remaining */}
                <div className="bg-white/20 rounded-lg p-4 mb-4">
                  <p className="text-xs uppercase tracking-wide mb-1 opacity-80">Deadline</p>
                  {(() => {
                    const remaining = getTimeRemaining(activeRequest.deadline);
                    return (
                      <p className={`text-2xl font-bold ${remaining.expired ? 'text-yellow-300' : ''}`}>
                        {remaining.text}
                      </p>
                    );
                  })()}
                </div>

                {/* Client ID */}
                <div className="bg-white/10 rounded-lg p-4 mb-4">
                  <p className="text-xs uppercase tracking-wide mb-1 opacity-80">{t('drugScreen.yourId')}</p>
                  <p className="text-2xl font-mono font-bold tracking-wider">{activeRequest.client_id}</p>
                </div>

                {/* Approved Labs */}
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-wide mb-2 opacity-80">{t('drugScreen.labs')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {AUSTRALIAN_LABS.filter(lab => lab.id !== 'other').slice(0, 4).map(lab => (
                      <div key={lab.id} className="bg-white/10 rounded-lg p-2 text-sm">
                        {lab.name}
                      </div>
                    ))}
                  </div>
                </div>

                {/* File Upload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="drug-screen-file-input"
                />

                {uploadedFile ? (
                  <div className="bg-white/20 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                      {uploadedFile.preview ? (
                        <img src={uploadedFile.preview} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                      ) : (
                        <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                          <FileText className="w-8 h-8" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium">{uploadedFile.name}</p>
                        <p className="text-xs opacity-80">Ready to submit</p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex gap-3">
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="flex-1 bg-white/20 hover:bg-white/30 text-white h-12"
                    data-testid="select-file-btn"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    {uploadedFile ? 'Change File' : t('drugScreen.scanOrPhoto')}
                  </Button>
                  
                  {uploadedFile && (
                    <Button
                      onClick={handleSubmitResults}
                      disabled={isUploading}
                      className="flex-1 bg-white text-red-600 hover:bg-red-50 h-12 font-bold"
                      data-testid="submit-results-btn"
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      {isUploading ? 'Submitting...' : 'Submit Results'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* No Active Request */}
          {!activeRequest && (
            <div className="bg-white rounded-xl p-8 mb-6 text-center" data-testid="no-active-request">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">No Active Requests</h2>
              <p className="text-gray-600">You don't have any pending drug screen requests.</p>
            </div>
          )}

          {/* History */}
          <div className="bg-white rounded-xl p-6" data-testid="screen-history">
            <h2 className="text-lg font-bold text-gray-800 mb-4 uppercase tracking-wide">Screening History</h2>
            
            {screenHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No previous screenings</p>
            ) : (
              <div className="space-y-3">
                {screenHistory.map((screen) => (
                  <div 
                    key={screen.screen_id}
                    className="border border-gray-200 rounded-lg p-4 flex items-center gap-4"
                  >
                    <div className={`
                      p-2 rounded-full
                      ${screen.status === 'verified' ? 'bg-green-100 text-green-600' : 
                        screen.status === 'submitted' ? 'bg-blue-100 text-blue-600' : 
                        'bg-gray-100 text-gray-600'}
                    `}>
                      {screen.status === 'verified' ? <CheckCircle className="w-5 h-5" /> : 
                       screen.status === 'submitted' ? <Clock className="w-5 h-5" /> : 
                       <FileText className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        {new Date(screen.requested_at).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-500">ID: {screen.client_id}</p>
                    </div>
                    <span className={`
                      text-xs font-bold px-3 py-1 rounded-full uppercase
                      ${screen.status === 'verified' ? 'bg-green-100 text-green-700' : 
                        screen.status === 'submitted' ? 'bg-blue-100 text-blue-700' : 
                        screen.result === 'positive' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'}
                    `}>
                      {screen.status === 'verified' && screen.result ? screen.result : screen.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default DrugScreenPage;
